import { type ReactElement, type ReactNode } from 'react'
import { DateTime } from 'luxon'
import { PhoneNumberInput } from 'app/components/terros'
import { PickUser } from 'app/components/pickers'
import { LockedFieldTooltip, PermissionCheckWrapper } from 'app/components/permissions'
import { TButton } from 'app/components/buttons'
import {
  CLOSING_STATUSES,
  type CompanyId,
  type LockableUserField,
  type UnsavedUser,
  type UserId,
  isUserId,
} from '@terros/common'
import { type UseFormReturnType } from '@mantine/form'
import { DateInput, type DateInputProps } from '@mantine/dates'
import { Group, Input, NumberInput, Select, SimpleGrid, TextInput } from '@mantine/core'
import {
  CLOSER_STATUS_ITEMS,
  DEFAULT_CLOSER_STATUS,
  useLoadedEntitlement,
  useLockedUserFields,
  usePermissionCheck,
  usePreference,
  useTranslation,
} from '@front/model'
import { UserCustomFields } from '../UserCustomFields'

type Props<T extends UnsavedUser> = {
  form: UseFormReturnType<T>
  companyId?: CompanyId
  requireManager?: boolean
  showClearArchivedManager?: boolean
}

export const UserProfileForm = <T extends UnsavedUser>({
  form,
  companyId,
  requireManager,
  showClearArchivedManager,
}: Props<T>): ReactElement => {
  const { getInputProps, setValues } = form
  const { t } = useTranslation()
  const isRecruiting = useLoadedEntitlement('recruiting')
  const { lockedFields } = useLockedUserFields(getUserId(form))
  const isLocked = (field: LockableUserField): boolean => lockedFields.includes(field)

  const clearArchivedManager = (): void => {
    setValues((prev) => ({ ...prev, managerId: undefined, hasManager: false }))
  }

  return (
    <>
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <LockedFieldTooltip locked={isLocked('lastName')}>
          <TextInput {...getInputProps('lastName')} withAsterisk label={t('Last Name')} placeholder={t('Doe')} />
        </LockedFieldTooltip>
        <LockedFieldTooltip locked={isLocked('firstName')}>
          <TextInput {...getInputProps('firstName')} withAsterisk label={t('First Name')} placeholder={t('John')} />
        </LockedFieldTooltip>
      </SimpleGrid>
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <LockedFieldTooltip locked={isLocked('preferredName')}>
          <TextInput {...getInputProps('preferredName')} label={t('Preferred Name')} placeholder={t('Johnny')} />
        </LockedFieldTooltip>
        <LockedFieldTooltip locked={isLocked('title')}>
          <TextInput {...getInputProps('title')} label={t('Title')} placeholder={t('Title')} />
        </LockedFieldTooltip>
      </SimpleGrid>
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <LockedFieldTooltip locked={isLocked('email')}>
          <TextInput {...getInputProps('email')} withAsterisk label={t('Email')} placeholder={t('your@example.com')} />
        </LockedFieldTooltip>
        <PhoneNumberInput {...getInputProps('phone')} label={t('Phone')} />
      </SimpleGrid>

      {!isRecruiting && (
        <Select
          {...getInputProps('closerStatus')}
          data={CLOSER_STATUS_ITEMS}
          label={t('Closer Status')}
          allowDeselect={false}
        />
      )}
      <CloserRank form={form} companyId={companyId} />
      <Input.Wrapper className='flex flex-col' {...getInputProps('managerId')}>
        <Group justify='space-between' wrap='nowrap'>
          <Input.Label required={requireManager}>{t('Manager')}</Input.Label>
          {showClearArchivedManager && (
            <TButton icon='person_remove' variant='text' onClick={clearArchivedManager}>
              {t('Clear')}
            </TButton>
          )}
        </Group>
        <PickUser
          companyId={companyId}
          drawerProps={{ id: 'manager-select' }}
          label={t('Choose Manager')}
          userId={getInputProps('managerId').value}
          onSelect={({ userId }) => {
            setValues((prev) => ({ ...prev, managerId: userId, hasManager: true }))
          }}
        />
      </Input.Wrapper>
      <UserCustomFields form={form} disabled={isLocked('customFields')} />
      <LockedFieldTooltip locked={isLocked('startDate')}>
        <StartDateInput {...form.getInputProps('startDate')} />
      </LockedFieldTooltip>
      <PermissionCheckWrapper permissionType='user:manage' targetId={getUserId(form)}>
        <LockedFieldTooltip locked={isLocked('clientUserId')}>
          <TextInput {...form.getInputProps('clientUserId')} label={t('External ID')} placeholder='123456' />
        </LockedFieldTooltip>
      </PermissionCheckWrapper>
    </>
  )
}

const CloserRank = <T extends UnsavedUser>({ form, companyId }: Props<T>): ReactNode => {
  const { data: hasPermission } = usePermissionCheck({
    permissionType: 'user:manage',
    targetId: [companyId, getUserId(form)],
  })
  const { t } = useTranslation()

  const { closerStatus } = form.values
  const { data: defaultCloserStatus = DEFAULT_CLOSER_STATUS, isLoading } = usePreference('closerStatus')
  const status = closerStatus ?? defaultCloserStatus
  if (!CLOSING_STATUSES.includes(status)) return
  if (!hasPermission) return
  if (isLoading) return

  return (
    <NumberInput
      value={form.values.closer?.order}
      onChange={(value) => {
        form.setFieldValue('closer', (prev) => ({
          ...prev,
          order: Number(value),
        }))
      }}
      label={t('Closer Rank')}
      description={t('Used to sort closers when selecting timeslots, lower numbers appear first')}
    />
  )
}

const getUserId = <T extends UnsavedUser>(form: UseFormReturnType<T>): UserId | undefined => {
  const userId = 'userId' in form.values ? form.values.userId : undefined
  if (isUserId(userId)) return userId
}

type StartDateInputProps = Omit<DateInputProps, 'value'> & { value?: Date | number | string | null }

const StartDateInput = ({ value, ...rest }: StartDateInputProps): ReactElement => {
  const { t } = useTranslation()
  const description = t('The date the user first started working')
  if (typeof value === 'number') {
    const date = DateTime.fromMillis(value, { zone: 'utc' }).toISODate()
    return <DateInput {...rest} description={description} label={t('Start Date')} value={date} />
  }

  return <DateInput {...rest} description={description} label={t('Start Date')} value={value} />
}
